import { z } from 'zod';

/* =========================================================================
 * Exam simulator — one mock test of Georgia's national English exam.
 *
 * Six tasks, fixed item counts (EXAM.md § The format):
 *   1 listening   8 MCQ A–D           2 matching    8 × paragraph A–F
 *   3 reading     8 MCQ A–D           4 vocabulary 12 gaps, 14-word bank A–N
 *   5 grammar    12 gaps × 4 options  6 dialogue    6 gaps, 8 lines A–H
 * = 54 points. No essay (Tornike, 2026-09-25).
 *
 * The structural rules that make a test fair — every evidence quote is
 * really in the text, each bank word is used once, gaps are numbered in
 * order — are enforced HERE, so the app refuses a broken test at load time
 * and `pnpm validate-content` refuses it at author time. Judgement calls
 * (is the answer the ONLY defensible one? is the English natural?) belong
 * to the reviewer agent, not the schema.
 *
 * Gap markers in running text are `{{n}}`, n = 1-based and in order.
 * Paragraphs inside a text are separated by a blank line.
 * ========================================================================= */

export const EXAM_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'] as const;

const letter4 = z.enum(['A', 'B', 'C', 'D']);
const letter6 = z.enum(['A', 'B', 'C', 'D', 'E', 'F']);
const letter8 = z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']);
const letter14 = z.enum(EXAM_LETTERS);

export type ExamLetter = (typeof EXAM_LETTERS)[number];

/** Index of a letter: A → 0. */
export function letterIndex(letter: string): number {
  return EXAM_LETTERS.indexOf(letter as ExamLetter);
}

const audioKeySchema = z
  .string()
  .regex(/^exam-\d{3}-[a-z0-9-]+\.mp3$/u, 'exam audio_key must look like exam-001-listening.mp3');

/** exam-001-listening.mp3, or a later take of it: exam-001-listening-v2.mp3.
 *  Clips are cached as immutable, so a changed clip gets a new key
 *  (scripts/exam-leadin.ts). */
function isListeningKeyFor(key: string, number: number): boolean {
  return new RegExp(`^exam-${String(number).padStart(3, '0')}-listening(-v[2-9]|-v[1-9]\\d+)?\\.mp3$`, 'u').test(key);
}

const text = z.string().trim().min(1);

/** A starred gloss under a text, as on the real paper: `* thirst: წყურვილი`. */
const glossSchema = z.object({ word: text, ka: text });

const option4 = z.tuple([text, text, text, text]);

/** Multiple choice with its proof: `evidence` is the exact span of the
 *  text or transcript that answers the question. */
const mcqSchema = z.object({
  question: text,
  options: option4,
  answer: letter4,
  evidence: text,
});

export const examListeningSchema = z.object({
  /** Shown in review only — the real paper never names the recording. */
  title: text,
  audio_key: audioKeySchema,
  /** Exactly what the narrator says. Shown after the test. */
  transcript: text,
  /** Who reads it: the speaker's voice must match the speaker (a talk by
   *  "Levan" is not read by a woman). female = the narrator voice (Aria),
   *  male = the Giorgi voice (Guy) — the two voices the lessons use. */
  voice: z.enum(['female', 'male']).default('female'),
  questions: z.array(mcqSchema).length(8),
});

export const examMatchingSchema = z.object({
  title: text,
  /** Paragraphs A–F, in order. */
  paragraphs: z.array(text).length(6),
  glossary: z.array(glossSchema).max(3).default([]),
  questions: z
    .array(z.object({ question: text, answer: letter6, evidence: text }))
    .length(8),
});

export const examReadingSchema = z.object({
  title: text.optional(),
  /** Optional italic lead-in, like "This is a personal story told by…". */
  intro: text.optional(),
  text,
  glossary: z.array(glossSchema).max(3).default([]),
  questions: z.array(mcqSchema).length(8),
});

export const examVocabularySchema = z.object({
  title: text,
  /** The word bank, alphabetical, lettered A–N by position. */
  words: z.array(text).length(14),
  /** Running text with `{{1}}` … `{{12}}`. */
  text,
  /** answers[i] = the bank letter for gap i+1. */
  answers: z.array(letter14).length(12),
});

export const examGrammarSchema = z.object({
  title: text,
  text,
  gaps: z
    .array(
      z.object({
        options: option4,
        answer: letter4,
        /** One short Georgian sentence on the rule, შენ-form. */
        why_ka: text,
      }),
    )
    .length(12),
});

const dialogueLineSchema = z.union([
  z.object({ speaker: z.union([z.literal(0), z.literal(1)]), text }),
  z.object({ speaker: z.union([z.literal(0), z.literal(1)]), gap: z.number().int().min(1).max(6) }),
]);

export const examDialogueSchema = z.object({
  title: text,
  speakers: z.tuple([text, text]),
  lines: z.array(dialogueLineSchema).min(8),
  /** Candidate lines A–H. */
  options: z.array(text).length(8),
  /** answers[i] = the option letter for gap i+1. */
  answers: z.array(letter8).length(6),
});

/* ------------------------- structural checks ------------------------- */

/** Evidence quotes are compared after normalising whitespace and curly
 *  quotes, so a line break in the source doesn't break the check. */
function norm(s: string): string {
  return s
    .replace(/[‘’]/gu, "'")
    .replace(/[“”]/gu, '"')
    .replace(/\s+/gu, ' ')
    .trim();
}

/** Gap numbers in the order they appear in `text`. */
export function gapNumbers(s: string): number[] {
  return [...s.matchAll(/\{\{(\d+)\}\}/gu)].map((m) => Number(m[1]));
}

function expectGaps(ctx: z.RefinementCtx, path: (string | number)[], s: string, count: number) {
  const found = gapNumbers(s);
  const want = Array.from({ length: count }, (_, i) => i + 1);
  if (found.join(',') !== want.join(',')) {
    ctx.addIssue({
      code: 'custom',
      path,
      message: `gap markers must be {{1}}…{{${count}}} once each, in order — found [${found.join(', ')}]`,
    });
  }
}

function expectDistinct(ctx: z.RefinementCtx, path: (string | number)[], items: readonly string[], what: string) {
  const seen = new Set<string>();
  for (const item of items) {
    const key = norm(item).toLowerCase();
    if (seen.has(key)) {
      ctx.addIssue({ code: 'custom', path, message: `${what} repeats "${item}"` });
    }
    seen.add(key);
  }
}

function expectEvidence(
  ctx: z.RefinementCtx,
  path: (string | number)[],
  evidence: string,
  source: string,
  where: string,
) {
  if (!norm(source).includes(norm(evidence))) {
    ctx.addIssue({
      code: 'custom',
      path,
      message: `evidence is not an exact quote from ${where}: "${evidence}"`,
    });
  }
}

export const examTestSchema = z
  .object({
    kind: z.literal('exam_test'),
    id: z.string().regex(/^test-\d{3}$/u, 'id must be test-NNN'),
    number: z.number().int().min(1).max(120),
    level: z.number().int().min(1).max(5),
    listening: examListeningSchema,
    matching: examMatchingSchema,
    reading: examReadingSchema,
    vocabulary: examVocabularySchema,
    grammar: examGrammarSchema,
    dialogue: examDialogueSchema,
  })
  .superRefine((t, ctx) => {
    if (t.id !== `test-${String(t.number).padStart(3, '0')}`) {
      ctx.addIssue({ code: 'custom', path: ['id'], message: `id ${t.id} does not match number ${t.number}` });
    }
    if (!isListeningKeyFor(t.listening.audio_key, t.number)) {
      ctx.addIssue({ code: 'custom', path: ['listening', 'audio_key'], message: 'audio_key must be exam-NNN-listening.mp3 (or -vN.mp3) for this test' });
    }

    // 1 — listening
    t.listening.questions.forEach((q, i) => {
      expectDistinct(ctx, ['listening', 'questions', i, 'options'], q.options, 'options');
      expectEvidence(ctx, ['listening', 'questions', i, 'evidence'], q.evidence, t.listening.transcript, 'the transcript');
    });

    // 2 — matching: the quote must come from the paragraph the key names
    t.matching.questions.forEach((q, i) => {
      const para = t.matching.paragraphs[letterIndex(q.answer)] ?? '';
      expectEvidence(ctx, ['matching', 'questions', i, 'evidence'], q.evidence, para, `paragraph ${q.answer}`);
    });

    // 3 — reading
    t.reading.questions.forEach((q, i) => {
      expectDistinct(ctx, ['reading', 'questions', i, 'options'], q.options, 'options');
      expectEvidence(ctx, ['reading', 'questions', i, 'evidence'], q.evidence, t.reading.text, 'the text');
    });

    // 4 — vocabulary: alphabetical bank, 12 gaps, each word at most once
    const words = t.vocabulary.words;
    const sorted = [...words].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
    if (sorted.join('|') !== words.join('|')) {
      ctx.addIssue({ code: 'custom', path: ['vocabulary', 'words'], message: 'word bank must be alphabetical (A–N by position)' });
    }
    expectDistinct(ctx, ['vocabulary', 'words'], words, 'word bank');
    expectGaps(ctx, ['vocabulary', 'text'], t.vocabulary.text, 12);
    expectDistinct(ctx, ['vocabulary', 'answers'], t.vocabulary.answers, 'answers (each bank word is used once)');

    // 5 — grammar
    expectGaps(ctx, ['grammar', 'text'], t.grammar.text, 12);
    t.grammar.gaps.forEach((g, i) => {
      expectDistinct(ctx, ['grammar', 'gaps', i, 'options'], g.options, 'options');
    });

    // 6 — dialogue: gaps 1–6 in order, all in ONE speaker's turns
    const gapLines = t.dialogue.lines.filter((l): l is { speaker: 0 | 1; gap: number } => 'gap' in l);
    const order = gapLines.map((l) => l.gap);
    if (order.join(',') !== '1,2,3,4,5,6') {
      ctx.addIssue({ code: 'custom', path: ['dialogue', 'lines'], message: `dialogue gaps must be 1…6 in order — found [${order.join(', ')}]` });
    }
    if (new Set(gapLines.map((l) => l.speaker)).size > 1) {
      ctx.addIssue({ code: 'custom', path: ['dialogue', 'lines'], message: 'all dialogue gaps must belong to the same speaker' });
    }
    t.dialogue.lines.forEach((l, i) => {
      const prev = t.dialogue.lines[i - 1];
      if (i > 0 && prev && prev.speaker === l.speaker) {
        ctx.addIssue({ code: 'custom', path: ['dialogue', 'lines', i], message: 'speakers must alternate' });
      }
    });
    expectDistinct(ctx, ['dialogue', 'options'], t.dialogue.options, 'options');
    expectDistinct(ctx, ['dialogue', 'answers'], t.dialogue.answers, 'answers (each line is used once)');
  });

export type ExamTest = z.infer<typeof examTestSchema>;
export type ExamListening = z.infer<typeof examListeningSchema>;
export type ExamMatching = z.infer<typeof examMatchingSchema>;
export type ExamReading = z.infer<typeof examReadingSchema>;
export type ExamVocabulary = z.infer<typeof examVocabularySchema>;
export type ExamGrammar = z.infer<typeof examGrammarSchema>;
export type ExamDialogue = z.infer<typeof examDialogueSchema>;

/** The six tasks, in paper order, with their points. */
export const EXAM_TASKS = [
  { key: 'listening', items: 8 },
  { key: 'matching', items: 8 },
  { key: 'reading', items: 8 },
  { key: 'vocabulary', items: 12 },
  { key: 'grammar', items: 12 },
  { key: 'dialogue', items: 6 },
] as const;

export type ExamTaskKey = (typeof EXAM_TASKS)[number]['key'];

export const EXAM_MAX_SCORE = EXAM_TASKS.reduce((n, t) => n + t.items, 0); // 54

/** 2 hours: the real exam's 3 hours include the essay we leave out. */
export const EXAM_DURATION_MS = 2 * 60 * 60 * 1000;
